import { MODE_TYPES } from '../../constants';

/**
 * Util function to map a location stop to a select options
 * @param {object} stop - Location stop object
 * @return {object} - Select option {label, value}
 */
const mapStopToOption = (stop) => {
  if (!stop) {
    return null;
  }
  return {
    label: `${stop.address?.address1}, ${stop.address?.city}, ${stop.address?.state}`,
    value: {
      city: stop.address?.city,
      state: stop.address?.state,
      locationNumber: stop.locationNumber,
      appointmentTime: stop.appointmentEnd,
    },
  };
};

/**
 * Gets a list of stops from the shipment information object
 * @param {object} shipmentInfo - Shipment info object
 * @return {object[]} - List of stops
 */
export const getStopsFromShipmentInformation = (shipmentInfo, status) => {
  if (!shipmentInfo) {
    return null;
  }
  const { origin, destination, inBetweenStops } = shipmentInfo;
  const stops = [
    origin,
    ...(inBetweenStops || []),
    destination,
  ];
  if (status === 'At Shipper') {
    return stops.filter((stop) => stop.isShipper === true);
  }

  if (status === 'At Consignee') {
    return stops.filter((stop) => stop.isConsignee === true);
  }
  return stops;
};

export const getLtlLocationOption = (shipmentInfo, status) => {
  if (status === 'Pickup') {
    return mapStopToOption(shipmentInfo?.origin);
  }
  return mapStopToOption(shipmentInfo?.destination);
};

/**
 * Maps stops from a shipment info object to an array of select options
 * @param {object} shipmentInfo - Shipment info object
 * @return {object[]} - Select options based on shipment stops.
 */
export const getLocationOptionsFromShipmentInformation = (shipmentInfo, status, mode) => {
  if (mode === MODE_TYPES.lessThanTruckload) {
    return [getLtlLocationOption(shipmentInfo, status)];
  }
  return getStopsFromShipmentInformation(shipmentInfo, status, mode).map(mapStopToOption);
};

/**
 * Finds the next location in a shipmentStops object based off
 * of location number, constructs a select option, and
 * returns it.
 * @param {object} shipmentInfo - Shipment info object
 * @return {object} - Select option of a location with a given locationNumber
 */
export const getNextLocationOption = (shipmentInfo) => {
  const options = getLocationOptionsFromShipmentInformation(shipmentInfo);
  return options.find((option) => option.value.locationNumber === shipmentInfo.nextLocationNumber);
};

/**
 * Finds the previous location in a shipmentStops object based off
 * of location number, constructs a select option, and
 * returns it.
 * @param {object} shipmentInfo - Shipment info object
 * @return {object} - Select option of a location with a given locationNumber
 */
export const getPreviousLocationOption = (shipmentInfo) => {
  const options = getLocationOptionsFromShipmentInformation(shipmentInfo);
  return options.find((option) => option.value.locationNumber === shipmentInfo.previousLocationNumber);
};

/**
 * Finds a location in a shipmentStops object based off
 * of location number, returns a string of the city and state
 * @param {object} shipmentInfo - Shipment info object
 * @return {string} - City and state from location with a given locationNumber
 */
export const getCityStateNextStop = (shipmentInfo) => {
  if (!shipmentInfo) {
    return '';
  }
  const nextStop = getNextLocationOption(shipmentInfo);
  if (!nextStop?.value?.city || !nextStop?.value?.state) {
    return 'N/A';
  }
  return `${nextStop?.value?.city}, ${nextStop?.value?.state}`;
};
